import { Plus, Trash2 } from "lucide-react";
import type { WorkflowConditionChoice } from "@proclaw/shared";
import { Field, Select, Textarea } from "./form-primitives";
import { ExpandableHighlightedTextarea } from "./expandable-highlighted-textarea";

function choiceId(): string {
  return `choice-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

export function ConditionConfig({
  data,
  onChange,
  readOnly,
  variables,
}: {
  data: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  readOnly: boolean;
  variables?: { value: string; label: string; hint?: string }[];
}) {
  const update = (patch: Record<string, unknown>) => onChange({ ...data, ...patch });
  const mode = data.mode === "multiple" ? "multiple" : "binary";
  const choices = Array.isArray(data.choices)
    ? (data.choices as WorkflowConditionChoice[])
    : [];

  const setMode = (nextMode: string) => {
    if (nextMode === "multiple") {
      const initialChoices =
        choices.length >= 2
          ? choices
          : [
              {
                id: "yes",
                label: "Path 1",
                expression: (data.expression as string) ?? "",
              },
              { id: choiceId(), label: "Path 2", expression: "" },
              { id: "no", label: "Otherwise", isDefault: true },
            ];
      update({ mode: "multiple", choices: initialChoices });
      return;
    }
    update({ mode: "binary" });
  };

  const setChoices = (nextChoices: WorkflowConditionChoice[]) => {
    const regularChoices = nextChoices.filter((choice) => !choice.isDefault);
    const defaultChoice =
      nextChoices.find((choice) => choice.isDefault) ??
      ({
        id: choiceId(),
        label: "Otherwise",
        isDefault: true,
      } satisfies WorkflowConditionChoice);
    update({
      choices: [
        ...regularChoices.map((choice, index) => ({
          ...choice,
          label: `Path ${index + 1}`,
          isDefault: false,
        })),
        {
          ...defaultChoice,
          label: "Otherwise",
          expression: "",
          isDefault: true,
        },
      ],
    });
  };

  const patchChoice = (id: string, patch: Partial<WorkflowConditionChoice>) => {
    setChoices(
      choices.map((choice) => (choice.id === id ? { ...choice, ...patch } : choice)),
    );
  };

  const addChoice = () => {
    if (choices.length >= 8) return;
    const next = {
      id: choiceId(),
      label: `Path ${choices.filter((choice) => !choice.isDefault).length + 1}`,
      expression: "",
    };
    const defaultIndex = choices.findIndex((choice) => choice.isDefault);
    if (defaultIndex === -1) {
      setChoices([...choices, next]);
      return;
    }
    setChoices([
      ...choices.slice(0, defaultIndex),
      next,
      ...choices.slice(defaultIndex),
    ]);
  };

  const regularChoiceCount = choices.filter((choice) => !choice.isDefault).length;

  return (
    <div className="space-y-4">
      <Field label="Branching">
        <Select
          value={mode}
          onChange={setMode}
          options={[
            { value: "binary", label: "Yes / No" },
            { value: "multiple", label: "Multiple choice" },
          ]}
          disabled={readOnly}
        />
      </Field>

      {mode === "binary" ? (
        <Field label="Expression">
          <ExpandableHighlightedTextarea
            value={(data.expression as string) ?? ""}
            onChange={(value) => update({ expression: value.replace(/\r?\n/g, " ") })}
            placeholder='{">":[{"var":"steps.score.outputs.score"},70]}'
            readOnly={readOnly}
            rows={1}
            variables={variables}
            title="Condition expression"
            singleLine
            className="h-[58px] font-mono text-xs"
          />
          <p className="mt-1 text-[11px] text-text-muted">
            A true result follows Yes; a false result follows No.
          </p>
        </Field>
      ) : (
        <div className="space-y-2.5">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-medium uppercase tracking-wide text-text-muted">
              Outcomes
            </p>
            {!readOnly && (
              <button
                type="button"
                onClick={addChoice}
                disabled={choices.length >= 8}
                className="flex items-center gap-1 rounded px-1.5 py-1 text-[11px] font-medium text-accent transition-colors hover:bg-accent/10 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Plus size={12} />
                Add path
              </button>
            )}
          </div>

          {choices.map((choice, index) => {
            const regularIndex = choices
              .slice(0, index + 1)
              .filter((item) => !item.isDefault).length;
            const pathLabel = choice.isDefault
              ? "Otherwise"
              : `Path ${regularIndex}`;
            return (
              <div
                key={choice.id}
                className="space-y-2 rounded-lg border border-border bg-background p-2.5"
              >
                <div className="flex items-center gap-2">
                  <span className="min-w-0 flex-1 text-[12px] font-semibold text-text-primary">
                    {pathLabel}
                  </span>
                  {!readOnly && !choice.isDefault && regularChoiceCount > 2 && (
                    <button
                      type="button"
                      onClick={() =>
                        setChoices(choices.filter((item) => item.id !== choice.id))
                      }
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-text-muted transition-colors hover:bg-red-500/10 hover:text-red-500"
                      aria-label={`Delete ${pathLabel}`}
                      title="Delete path and its connection"
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>

                {choice.isDefault ? (
                  <p className="text-[11px] text-text-muted">
                    Used when none of the paths above match.
                  </p>
                ) : (
                  <ExpandableHighlightedTextarea
                    value={choice.expression ?? ""}
                    onChange={(value) =>
                      patchChoice(choice.id, {
                        expression: value.replace(/\r?\n/g, " "),
                      })
                    }
                    placeholder='{"==":[{"var":"steps.assess.outputs.status"},"approved"]}'
                    readOnly={readOnly}
                    rows={1}
                    variables={variables}
                    title={`${pathLabel} expression`}
                    singleLine
                    className="h-[58px] font-mono text-xs"
                  />
                )}
              </div>
            );
          })}

          <p className="text-[11px] text-text-muted">
            Rules are checked from top to bottom. The first match chooses its
            connection.
          </p>
        </div>
      )}

      <Field label="Description">
        <Textarea
          value={(data.description as string) ?? ""}
          onChange={(value) => update({ description: value })}
          placeholder="Explain the branching logic"
          readOnly={readOnly}
          rows={3}
        />
      </Field>
    </div>
  );
}
